// Status-pill driver. UI_LOADING_* (blocking phase) and UI_TASK_* (background
// work) both render into #task-indicator. The two intent groups stay distinct
// so call sites still convey intent; only the rendering is shared.

import { INTENTS } from '../store/intents';
import type { Store } from '../store/store';
import type { State } from '../store/schema';

interface DismissEl extends HTMLElement { _bound?: boolean }

let depth = 0;          // nested UI_LOADING_SHOW → HIDE pairs in bootstrap
let baseMsg = '';       // current loading message minus any " NN%" suffix

function el(): HTMLElement | null { return document.getElementById('task-indicator'); }
function textEl(): HTMLElement | null { return document.getElementById('task-indicator-text'); }

function show(mode: 'loading' | 'task', msg: string | undefined): void {
    const root = el();
    if (!root) return;
    root.dataset.mode = mode;
    if (typeof msg === 'string') {
        const t = textEl();
        if (t) t.textContent = msg;
    }
    if (root.style.display !== 'flex') root.style.display = 'flex';
    if (mode === 'task') {
        const dismiss = document.getElementById('task-indicator-dismiss') as DismissEl | null;
        if (dismiss && !dismiss._bound) {
            dismiss._bound = true;
            dismiss.addEventListener('click', () => { root.style.display = 'none'; });
        }
    }
}

function applyLoadingShow(msg: string | undefined): void {
    depth += 1;
    // Outermost SHOW resets progress state; nested SHOWs (e.g. a phase rename
    // mid-stream) keep whatever percent was already ticking.
    if (depth === 1) baseMsg = typeof msg === 'string' ? msg : '';
    else if (typeof msg === 'string') baseMsg = msg;
    show('loading', baseMsg);
}

function applyLoadingHide(): void {
    depth = Math.max(depth - 1, 0);
    if (depth === 0) {
        const root = el();
        if (root) root.style.display = 'none';
        baseMsg = '';
    }
}

function applyProgress(value: number): void {
    if (depth === 0) return;
    const clamped = Math.max(0, Math.min(1, Number(value) || 0));
    const pct = Math.round(clamped * 100);
    const t = textEl();
    if (t) t.textContent = baseMsg ? `${baseMsg} ${pct}%` : `${pct}%`;
}

function applyTaskShow(msg: string | undefined): void { show('task', msg); }

function applyTaskHide(): void {
    const root = el();
    if (root) root.style.display = 'none';
}

export function installLoadingDriver(store: Store<State>): () => void {
    const offs = [
        store.onIntent(INTENTS.UI_LOADING_SHOW, (p) => applyLoadingShow(p && p.msg)),
        store.onIntent(INTENTS.UI_LOADING_HIDE, () => applyLoadingHide()),
        store.onIntent(INTENTS.UI_LOADING_PROGRESS, (p) => applyProgress(p ? p.value : 0)),
        store.onIntent(INTENTS.UI_TASK_SHOW, (p) => applyTaskShow(p && p.msg)),
        store.onIntent(INTENTS.UI_TASK_HIDE, () => applyTaskHide()),
    ];
    return () => offs.forEach((off) => off());
}
